import {
  chooseTorchOokTiming,
  encodeTorchOokFrame,
  TORCH_OOK_PROFILE,
} from '../protocol/torch-ook.js';

const MIN_MEASURED_CYCLES = 20;

/**
 * Drives a real MediaStreamTrack torch using an experimental, measured phone
 * profile. This controller does not claim C-OOK or IEEE conformance.
 */
export class TorchTransmitter {
  constructor(track, {
    now = () => globalThis.performance?.now?.() ?? Date.now(),
    sleep = defaultSleep,
    visibilityTarget = globalThis.document || null,
  } = {}) {
    if (!track || typeof track.applyConstraints !== 'function') {
      throw new TypeError('Une MediaStreamTrack avec applyConstraints est requise');
    }
    this.track = track;
    this.now = now;
    this.sleep = sleep;
    this.visibilityTarget = visibilityTarget;
    this.lastBenchmark = null;
    this.lastTiming = null;
    this.busy = false;
  }

  describe() {
    let capabilities = null;
    let settings = null;
    try { capabilities = this.track.getCapabilities?.() || null; } catch {}
    try { settings = this.track.getSettings?.() || null; } catch {}
    return {
      profile: TORCH_OOK_PROFILE,
      standardConformance: 'none-claimed',
      live: this.track.readyState !== 'ended',
      torchAdvertised: torchCapability(capabilities),
      capabilities,
      settings,
      benchmark: this.lastBenchmark,
      timing: this.lastTiming,
    };
  }

  /**
   * Measures ON and OFF independently. Warm-up transitions are deliberately
   * excluded and at least 20 samples are required in each direction.
   */
  async benchmark({
    warmupCycles = 3,
    measuredCycles = MIN_MEASURED_CYCLES,
    signal = null,
    deadlineMs = this.now() + 60_000,
    onProgress = () => {},
  } = {}) {
    this.#assertIdle();
    assertIntegerAtLeast(warmupCycles, 1, 'warmupCycles');
    assertIntegerAtLeast(measuredCycles, MIN_MEASURED_CYCLES, 'measuredCycles');
    this.#assertUsableTrack();
    const guard = new OperationGuard({
      signal,
      deadlineMs,
      now: this.now,
      visibilityTarget: this.visibilityTarget,
    });
    this.busy = true;
    const startedAt = this.now();
    const onLatenciesMs = [];
    const offLatenciesMs = [];

    try {
      await runWithTorchOff(this.track, guard, async () => {
        await applyTorch(this.track, false, guard, this.now, 'initial-off');
        for (let cycle = 0; cycle < warmupCycles; cycle += 1) {
          await applyTorch(this.track, true, guard, this.now, 'warmup-on');
          await applyTorch(this.track, false, guard, this.now, 'warmup-off');
          onProgress({ stage: 'warmup', cycle: cycle + 1, total: warmupCycles });
        }
        for (let cycle = 0; cycle < measuredCycles; cycle += 1) {
          const on = await applyTorch(this.track, true, guard, this.now, 'benchmark-on');
          onLatenciesMs.push(on.latencyMs);
          const off = await applyTorch(this.track, false, guard, this.now, 'benchmark-off');
          offLatenciesMs.push(off.latencyMs);
          onProgress({ stage: 'measure', cycle: cycle + 1, total: measuredCycles, on, off });
        }
      }, this.now);

      const on = summarizeDirection(onLatenciesMs);
      const off = summarizeDirection(offLatenciesMs);
      this.lastBenchmark = Object.freeze({
        profile: TORCH_OOK_PROFILE,
        standardConformance: 'none-claimed',
        warmupCycles,
        measuredCycles,
        startedAt,
        completedAt: this.now(),
        on,
        off,
        directionalP95Ms: Math.max(on.p95Ms, off.p95Ms),
        promiseTimingOnly: true,
        warning: 'applyConstraints mesure la promesse pilote, pas directement le front lumineux.',
      });
      return this.lastBenchmark;
    } finally {
      guard.close();
      this.busy = false;
    }
  }

  selectTiming({
    receiverFps,
    frameIntervalP95Ms = null,
    benchmark = this.lastBenchmark,
    ...options
  } = {}) {
    if (!benchmark || !Number.isFinite(benchmark.directionalP95Ms)) {
      throw operationError('BENCHMARK_REQUIRED', 'Bench torch directionnel requis avant le calcul de cadence');
    }
    this.lastTiming = chooseTorchOokTiming({
      torchP95Ms: benchmark.directionalP95Ms,
      receiverFps,
      frameIntervalP95Ms,
      ...options,
    });
    return this.lastTiming;
  }

  /**
   * Sends one CRC-protected envelope. Chip boundaries are scheduled against a
   * fixed origin; apply latency therefore cannot accumulate from chip to chip.
   */
  async transmit(envelopeBytes, {
    receiverFps,
    frameIntervalP95Ms = null,
    timing = null,
    benchmark = this.lastBenchmark,
    timingOptions = {},
    signal = null,
    deadlineMs = null,
    maxTransitionLatenessRatio = 1,
    onProgress = () => {},
  } = {}) {
    this.#assertIdle();
    this.#assertUsableTrack();
    if (!timing) {
      timing = this.selectTiming({
        receiverFps,
        frameIntervalP95Ms,
        benchmark,
        ...timingOptions,
      });
    }
    if (!Number.isFinite(timing?.chipMs) || timing.chipMs <= 0) {
      throw new RangeError('timing.chipMs invalide');
    }
    if (!Number.isFinite(maxTransitionLatenessRatio) || maxTransitionLatenessRatio <= 0) {
      throw new RangeError('maxTransitionLatenessRatio doit être > 0');
    }

    const frame = encodeTorchOokFrame(envelopeBytes, { chipMs: timing.chipMs });
    const startedAt = this.now();
    const effectiveDeadline = deadlineMs ?? (
      startedAt + frame.frameDurationMs + Math.max(2_000, frame.frameDurationMs * 0.20)
    );
    const guard = new OperationGuard({
      signal,
      deadlineMs: effectiveDeadline,
      now: this.now,
      visibilityTarget: this.visibilityTarget,
    });
    const transitions = [];
    let currentState = false;
    this.busy = true;

    try {
      await runWithTorchOff(this.track, guard, async () => {
        await applyTorch(this.track, false, guard, this.now, 'transmit-initial-off');
        const frameStartedAt = this.now();
        const frameEndsAt = frameStartedAt + frame.frameDurationMs;

        for (let chipIndex = 0; chipIndex < frame.chips.length; chipIndex += 1) {
          const scheduledAt = frameStartedAt + chipIndex * frame.chipMs;
          await waitUntil(scheduledAt, guard, this.now, this.sleep);
          const wantedState = Boolean(frame.chips[chipIndex]);
          if (wantedState !== currentState) {
            const transition = await applyTorch(
              this.track,
              wantedState,
              guard,
              this.now,
              wantedState ? 'data-on' : 'data-off',
            );
            const latenessMs = transition.completedAt - scheduledAt;
            const record = { chipIndex, scheduledAt, latenessMs, ...transition };
            transitions.push(record);
            if (latenessMs > frame.chipMs * maxTransitionLatenessRatio) {
              throw operationError(
                'TIMING_OVERRUN',
                `Transition ${chipIndex} en retard de ${round(latenessMs)} ms pour un chip de ${frame.chipMs} ms`,
                record,
              );
            }
            currentState = wantedState;
          }
          onProgress({
            stage: 'transmit',
            chipIndex,
            chipCount: frame.chips.length,
            state: wantedState,
            scheduledAt,
          });
        }
        await waitUntil(frameEndsAt, guard, this.now, this.sleep);
      }, this.now);

      const completedAt = this.now();
      const lateness = transitions.map((entry) => Math.max(0, entry.latenessMs));
      return {
        ok: true,
        code: 'SENT',
        profile: TORCH_OOK_PROFILE,
        standardConformance: 'none-claimed',
        timing,
        frame,
        startedAt,
        completedAt,
        elapsedMs: completedAt - startedAt,
        transitionCount: transitions.length,
        transitionLateness: summarizeDirection(lateness.length ? lateness : [0]),
        transitions,
      };
    } finally {
      guard.close();
      this.busy = false;
    }
  }

  async forceOff() {
    const startedAt = this.now();
    try {
      await this.track.applyConstraints({ advanced: [{ torch: false }] });
      return { ok: true, latencyMs: this.now() - startedAt };
    } catch (error) {
      throw operationError('TORCH_OFF_FAILED', errorMessage(error), { cause: error });
    }
  }

  #assertIdle() {
    if (this.busy) throw operationError('BUSY', 'Une opération torch est déjà en cours');
  }

  #assertUsableTrack() {
    if (this.track.readyState === 'ended') throw operationError('TRACK_ENDED', 'La piste caméra est terminée');
    let capabilities = null;
    try { capabilities = this.track.getCapabilities?.() || null; } catch (error) {
      throw operationError('CAPABILITIES_FAILED', errorMessage(error), { cause: error });
    }
    if (!torchCapability(capabilities)) {
      throw operationError('TORCH_UNAVAILABLE', 'La piste ne publie pas la contrainte torch');
    }
  }
}

export function summarizeTorchDirections(onLatenciesMs, offLatenciesMs) {
  const on = summarizeDirection(onLatenciesMs);
  const off = summarizeDirection(offLatenciesMs);
  return { on, off, directionalP95Ms: Math.max(on.p95Ms, off.p95Ms) };
}

function torchCapability(capabilities) {
  const value = capabilities?.torch;
  return value === true || (Array.isArray(value) && value.includes(true));
}

async function runWithTorchOff(track, guard, task, now) {
  let result;
  let primaryError = null;
  try {
    result = await task();
  } catch (error) {
    primaryError = error;
  }

  let offError = null;
  try {
    const startedAt = now();
    await track.applyConstraints({ advanced: [{ torch: false }] });
    void startedAt;
  } catch (error) {
    offError = operationError('TORCH_OFF_FAILED', errorMessage(error), { cause: error });
  }
  if (primaryError) {
    if (offError) primaryError.offError = offError;
    throw primaryError;
  }
  if (offError) throw offError;
  guard.check();
  return result;
}

async function applyTorch(track, enabled, guard, now, direction) {
  guard.check();
  if (track.readyState === 'ended') throw operationError('TRACK_ENDED', 'La piste caméra est terminée');
  const requestedAt = now();
  try {
    await track.applyConstraints({ advanced: [{ torch: enabled }] });
  } catch (error) {
    throw operationError('TORCH_APPLY_FAILED', `${direction}: ${errorMessage(error)}`, {
      direction,
      enabled,
      cause: error,
    });
  }
  const completedAt = now();
  guard.check();
  return {
    direction,
    enabled,
    requestedAt,
    completedAt,
    latencyMs: completedAt - requestedAt,
  };
}

async function waitUntil(targetMs, guard, now, sleep) {
  while (true) {
    guard.check();
    const current = now();
    if (current >= targetMs) return;
    const wakeAt = Math.min(targetMs, guard.deadlineMs);
    await guard.raceSleep(sleep, Math.max(0, wakeAt - current));
  }
}

class OperationGuard {
  constructor({ signal, deadlineMs, now, visibilityTarget }) {
    this.signal = signal;
    this.deadlineMs = Number.isFinite(deadlineMs) ? deadlineMs : Infinity;
    this.now = now;
    this.visibilityTarget = visibilityTarget;
    this.abortReason = null;
    this.listeners = new Set();
    this.onSignalAbort = () => this.abort('ABORTED', 'Opération annulée');
    this.onVisibility = () => {
      if (this.visibilityTarget?.visibilityState === 'hidden') {
        this.abort('PAGE_HIDDEN', 'Page masquée pendant une opération torch');
      }
    };
    signal?.addEventListener?.('abort', this.onSignalAbort, { once: true });
    visibilityTarget?.addEventListener?.('visibilitychange', this.onVisibility);
    if (signal?.aborted) this.onSignalAbort();
    this.onVisibility();
  }

  abort(code, message) {
    if (this.abortReason) return;
    this.abortReason = operationError(code, message);
    for (const listener of this.listeners) listener(this.abortReason);
    this.listeners.clear();
  }

  check() {
    if (this.abortReason) throw this.abortReason;
    if (this.now() >= this.deadlineMs) {
      throw operationError('DEADLINE_EXCEEDED', 'Échéance de l’opération torch dépassée');
    }
  }

  raceSleep(sleep, delayMs) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const onAbort = (error) => {
        if (settled) return;
        settled = true;
        reject(error);
      };
      this.listeners.add(onAbort);
      Promise.resolve()
        .then(() => sleep(delayMs))
        .then(() => {
          if (settled) return;
          settled = true;
          this.listeners.delete(onAbort);
          resolve();
        }, (error) => {
          if (settled) return;
          settled = true;
          this.listeners.delete(onAbort);
          reject(error);
        });
    });
  }

  close() {
    this.signal?.removeEventListener?.('abort', this.onSignalAbort);
    this.visibilityTarget?.removeEventListener?.('visibilitychange', this.onVisibility);
    this.listeners.clear();
  }
}

function summarizeDirection(values) {
  const sorted = [...values].filter((value) => Number.isFinite(value) && value >= 0).sort((a, b) => a - b);
  if (!sorted.length) throw new RangeError('aucune latence valide');
  return Object.freeze({
    count: sorted.length,
    minMs: sorted[0],
    p50Ms: quantile(sorted, 0.50),
    p95Ms: quantile(sorted, 0.95),
    maxMs: sorted.at(-1),
    meanMs: sorted.reduce((sum, value) => sum + value, 0) / sorted.length,
  });
}

function quantile(sorted, ratio) {
  const position = (sorted.length - 1) * ratio;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  const weight = position - lower;
  return sorted[lower] + (sorted[upper] - sorted[lower]) * weight;
}

function defaultSleep(delayMs) {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

function operationError(code, message, details = null) {
  const error = new Error(message);
  error.name = 'TorchOperationError';
  error.code = code;
  if (details) error.details = details;
  return error;
}

function errorMessage(error) {
  return error instanceof Error && error.message ? error.message : String(error || 'erreur inconnue');
}

function assertIntegerAtLeast(value, minimum, name) {
  if (!Number.isInteger(value) || value < minimum) throw new RangeError(`${name} doit être >= ${minimum}`);
}

function round(value) {
  return Math.round(value * 1000) / 1000;
}
